import React from "react";
import { Link } from "react-router-dom";

/**
 * ButtonGroup component that wraps children components, typically buttons, and dynamically modifies the className of the first child.
 * 
 * @component
 * @param {Object} props - The props object.
 * @param {React.ReactNode} props.children - The child components to be wrapped inside the ButtonGroup.
 * @returns {JSX.Element} The rendered ButtonGroup component.
 */
export const ButtonGroup = ({ children }) => {
    return (
        <div className="button-groups">
            {React.Children.map(children, (child, index) => {
                if (React.isValidElement(child)) {
                    let className = child.props?.className === undefined ? [] : child.props.className.split(' ').filter(item => item !== '');
                    if (index === 0) {
                        className.push('button-primary');
                    }
                    return React.cloneElement(child, { className: className.join(' ') });
                }
                return child;
            })}
        </div>
    );
};

/**
 * Button component used within the ButtonGroup component.
 * Renders a `Link` component from `react-router-dom` with specified props.
 * 
 * @component
 * @param {Object} props - The props object.
 * @param {string} props.to - The destination path to which the link should navigate.
 * @param {string} props.text - The text to be displayed inside the button.
 * @param {boolean} [props.replace=false] - If true, the navigation will replace the current entry in the history stack instead of adding a new one.
 * @param {string} [props.className=""] - Optional additional className to apply to the button.
 * @returns {JSX.Element} The rendered Button component.
 */
ButtonGroup.Button = ({ to, text, replace = false, className = "" }) => {
    return (
        <Link to={to} replace={replace} className={`button ${className}`}>
            {text}
        </Link>
    );
};

